import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button, ConfirmDialog, Panel, PanelBody, PanelFooter, PanelHeader } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ExternalTextLink, TextLink } from '@/components/ui/TextLink';
import { XeroStatusBadge } from '@/components/xero/XeroStatusBadge';
import { ApiError, api } from '@/lib/api';
import {
  COMPANY_APPROVAL_REJECTED_CODE,
  COMPANY_APPROVAL_UNREADABLE_CODE,
  COMPANY_NOT_APPROVED_CODE,
  billingGateRefusalOf,
} from '@/lib/company-approval';
import { documentCustomerName } from '@/lib/company-names';
import { cx } from '@/lib/cx';
import { formatDateTime, orDash } from '@/lib/format';
import {
  CONTACTS_ARE_NOT_XERO_CONTACTS,
  XERO_ENTITIES,
  canLink,
  canPush,
  canRetry,
  createConflictOf,
  isLinked,
  pushIntentFor,
  isSilentlyNotSyncing,
  isRestorableInXero,
  isStoodDownAsGone,
  quoteRefusalOf,
  refusalOriginOf,
  statusDetail,
  xeroDeepLink,
  xeroErrorLines,
  type XeroConnection,
  type XeroCreateConflict,
  type XeroEntity,
  type XeroLink,
  type XeroQuoteRefusal,
  type XeroRefusalDestination,
  type XeroTaxRefusalOrigin,
  type XeroTaxSource,
} from '@/lib/xero';

export interface XeroPanelProps {
  entity: XeroEntity;
  recordId: number;
  recordName: string;
  link: XeroLink;
  connection: XeroConnection;
  /** Called after any mutation so the parent can refetch the record. */
  onChanged: () => void;
  /**
   * Gate writes. Pass `ability.canUpdate(<THIS RECORD'S module>)` from the call
   * site — `companies`, `invoices` or `quotes`, matching `entity`. *SUPERSEDED:
   * "Pass `ability.canUpdate('companies')`",* which named one mount's answer as
   * though it were all three.
   *
   * ⛔ **NOT `integrations`.** That module gates the connect and disconnect
   * controls on `/integrations`, where this panel is not mounted. Everything
   * here — push, unlink, resolve a conflict — is a write on the record itself,
   * so it follows the record's own module and its own role guard.
   */
  canManage: boolean;
  /**
   * Opens the "link to an existing Xero contact" picker (lives with the parent —
   * it's a search surface with its own fetches). Omitted where a page hasn't
   * wired one up yet: the button is simply not offered.
   *
   * Takes no search term — `XeroLinkModal` seeds its query from its own
   * `recordName` prop, and the page hands the same name to both components, so
   * opening the picker from the `name-taken` banner lands pre-searched on
   * exactly the name Xero refused. A page must never give the panel and the
   * modal different names for the same record.
   */
  onRequestLink?: () => void;
  /**
   * The company this record bills, used to route any refusal whose fix touches
   * the company record (`company-record`, `quote-form-or-company`) — links to
   * `/companies/{id}` and names the company instead of saying "the company".
   *
   * Absent, falls back to `the company this {noun} bills`
   * (`XERO_ENTITIES[entity].label`). Optional because a quote can have no
   * company at all (`XERO_QUOTE_NO_COMPANY` is itself one of the refusals), and
   * a page that hasn't wired this up must still render every other refusal.
   * Never invents an id — an absent company just drops the link.
   *
   * Named by its Billing name (the Xero Contact Name) via
   * `documentCustomerName`, owner 2026-10-25: Xero UI names the Xero contact.
   */
  company?: { id: number; name: string; billingName?: string | null } | null;
  /**
   * Offered when a refusal is fixable inline: the two Sales-defaults codes
   * (`XERO_NO_TAX_TYPE`, `XERO_NO_LINE_AMOUNT_TYPE`). The page owns the dialog;
   * the panel only asks for it.
   *
   * Absent, those two fall back to the link to `/companies/{id}` — a page that
   * has not wired the dialog must still route the reader somewhere real.
   */
  onFixCompanyDefaults?: () => void;
  /**
   * Render WITHOUT the outer `Panel` and `PanelHeader`, for a host that draws
   * its own section border and heading (the company page's foldable sections).
   * Default `false`, so every existing call site renders exactly as before.
   *
   * When `true`: the header's actions (the collapsed-only status badge and the
   * Show/Hide toggle) move into a right-aligned toolbar row at the top of the
   * body, gated exactly as today; the header's description — the tenant line,
   * and the unresolved sentence while folded — follows as its own line, because
   * the tenant is never left implicit beside a button that writes to it and the
   * folded "not reaching Xero" warning is the one thing a fold may not hide.
   * The fold, every state, sentence, gate, callback, refusal routing and the
   * three confirmations are unchanged in both modes; `aria-controls` still
   * points at the body, which is rendered (hidden) either way, and nothing
   * references the heading id that is no longer drawn.
   */
  embedded?: boolean;
}

/**
 * Which entities reach Xero by being SAVED, and therefore no longer carry a
 * create button here.
 *
 * Owner's decision, 2026-08-30: *"on quotes and invoices the data is directly
 * getting synced to Xero … remove the Push to Xero button on both."* The server
 * pushes inside every save of a quote or an invoice and — as of the same
 * decision — CREATES the Xero document on the first one, so a second, manual
 * "Create in Xero" would be a person doing by hand what the save already did,
 * on an endpoint that is create-**or**-update: press it after a save has landed
 * and it updates; press it after a save was skipped and it creates. One button,
 * two outcomes, no way to tell them apart from the label.
 *
 * ⚠️ **`companies` MUST STAY `false`, and it is not symmetry waiting to be
 * finished.** A company is NOT auto-created on save — `createCompany` enqueues
 * nothing and only `updateCompany` pushes, deliberately, because a CRM row may
 * be a typo, a duplicate or a test record and there is no undo in Xero a CRM
 * delete can reach. Flipping this entry would take the only path a customer has
 * into the accounting system, which every quote and invoice depends on.
 *
 * An exhaustive `Record` rather than a `Set` or an `entity === 'quotes' ||` test:
 * a fifth entity has to be classified before this file compiles, where a set
 * would silently default it to "still a button".
 *
 * ⚠️ **`POST /xero/create/{entity}/{id}` is UNCHANGED and stays as the
 * operator recovery path.** This removes the affordance, not the capability.
 *
 * ## ⚠️ EXTENDED 2026-08-31 — IT NOW WITHHOLDS THE MANUAL SEND AND THE RETRY TOO
 *
 * Owner's decision, asked twice: *"remove this Push to Xero button from both
 * invoices and quotes, because auto saving is working."* An entity that reaches
 * Xero by being saved gets no manual push of ANY kind from this panel — not
 * Create, not "Send to Xero", not Retry.
 *
 * ⚠️ **Those last two were already unreachable for quotes and invoices, and
 * INCIDENTALLY so.** Both hang off `capabilities.sync`, which is `false`
 * because the backend's worker refuses quote and invoice jobs outright
 * (`409 XERO_PUSH_IS_INLINE`) — a fact about which ENDPOINT serves the push,
 * not a decision about whether a person may press a button. Flip that flag when
 * `/xero/sync` learns to queue them and "Send to Xero" reappears on both, which
 * is the exact control the owner has now asked to be removed twice. Gating on
 * `CREATED_BY_SAVING` records the decision where the decision is, so it
 * survives the capability moving under it.
 *
 * 🔴 **THE GAP, STATED RATHER THAN SOLVED.** Retry was the manual re-send for a
 * `failed` link. There is now no manual retry for a quote or an invoice: the
 * way to retry is to save the document again, and a save re-pushes. But a
 * document whose push failed and which nobody edits again **stays stale in
 * Xero, with nothing on screen offering to resend it.** Accepted, not
 * overlooked, and reported to the owner. `POST /xero/create/{entity}/{id}`
 * remains the operator recovery path.
 */
const CREATED_BY_SAVING: Record<XeroEntity, boolean> = {
  companies: false,
  contacts: false,
  quotes: true,
  invoices: true,
};

/** Which mutation is in flight. One at a time, so this is a value, not a set. */
type PendingAction = 'create' | 'push' | 'unlink' | 'keep-emerge' | 'keep-xero';

/** Which confirmation is open. Every one of these overwrites or detaches. */
type OpenConfirm = 'unlink' | 'keep-emerge' | 'keep-xero';

/**
 * The fold is a per-user preference, not a property of the record — persisted
 * like other remembered chrome state (see `useSidebarCollapsed`), read during
 * initial state so nothing flashes open then shut. Anything other than the
 * one "open" value reads as collapsed, which is also the first-visit default.
 */
const COLLAPSED_KEY = 'emerge.xero.panelCollapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) !== '0';
  } catch {
    // Storage throws outright in some private-browsing modes.
    return true;
  }
}

function useXeroPanelCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // Persisting is a convenience; the session still works without it.
    }
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((prev) => !prev), []);

  return [collapsed, toggle];
}

/**
 * The one sentence a folded panel still has to say, or null when nothing
 * needs saying. A fold that hides "this record is not reaching Xero" is worse
 * than the clutter it removes, so the two silent states and an outright
 * refusal are lifted into the header. `unlinked` is not one of them — it's
 * the resting state of most records and nothing has failed. Wording matches
 * the body's so expanding never reads as a different claim.
 */
function unresolvedLine(link: XeroLink): string | null {
  if (isSilentlyNotSyncing(link)) return 'Changes are not reaching Xero.';
  if (link.status === 'failed' || link.error) return 'Xero refused the last send.';
  return null;
}

/**
 * Where a company-approval refusal sends the reader: the company record.
 *
 * ⚠️ **The three codes are matched by `billingGateRefusalOf` and are
 * deliberately NOT entries in `XERO_QUOTE_REFUSALS`.** They belong to the
 * billing gate, not to the Xero push — the same 409s refuse a quote create, an
 * invoice create and both deal conversions, none of which goes anywhere near
 * this map. A second copy of the code list here would be a second matcher to
 * keep in step with `@/lib/company-approval`, and the one that drifted would
 * fail by silently falling through to the generic banner: a working panel,
 * nothing thrown, and the reader sent nowhere. So the classification stays in
 * one module and only the ROUTING lives here.
 *
 * `verbatim` because the server's sentence is the whole content — it names the
 * company, says an approver holds a link, and says the rest of the company
 * still works. `CLAUDE.md`'s rule is exact: surface it and keep no parallel
 * sentence of ours to drift from it.
 *
 * ⚠️ `retryable: false` is the literal the interface requires and is also true
 * of all three: pressing Create again reproduces the refusal until somebody
 * acts on the company. The gate's own `terminal` flag (which of the three
 * clears by waiting) is not read here — the banner's tone follows the
 * destination, and the server's message is what says which state this is.
 */
const BILLING_GATE_REFUSAL: XeroQuoteRefusal = {
  destination: 'company-record',
  retryable: false,
  verbatim: true,
};

/**
 * The Xero link on a single record: where it stands, and what can be done
 * about it. Written once for all four entities — everything it branches on
 * comes from the registry and the helpers in `@/lib/xero`, never from the
 * entity name — so a fifth entity is a registry flag, not a second panel.
 */
export function XeroPanel({
  entity,
  recordId,
  recordName,
  link,
  connection,
  onChanged,
  canManage,
  onRequestLink,
  company,
  onFixCompanyDefaults,
  embedded = false,
}: XeroPanelProps) {
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [error, setError] = useState<string[] | null>(null);
  /*
   * Which terminal `create` conflict the banner is reporting, if any.
   *
   * ⚠️ No branch of this ever offers a retry. The two 409s are refusals, not
   * transient failures: an existing link does not lapse and a taken name never
   * comes free, so a second attempt fails identically. What differs is the
   * resolving action — unlink for one, the link picker for the other —
   * switched on `error.code` (`XERO_ALREADY_LINKED` / `XERO_CONTACT_NAME_TAKEN`)
   * via `createConflictOf`; `'other'` is the safe fallback for anything
   * unrecognised.
   */
  const [conflict, setConflict] = useState<XeroCreateConflict | null>(null);
  /*
   * A refusal this app has been TAUGHT, and where it sends the reader.
   *
   * The code is kept beside the classification because the destination alone
   * can't pick the sentence — several codes share a destination but name
   * different things to fix. `null` covers both "nothing failed" and "an
   * unrecognised code" (expected for anything the backend adds later); both
   * fall through to the generic banner, which shows the server's own message.
   *
   * `origin` is WHICH record supplied the tax code, when the server says.
   * `null` where it doesn't (older server, no provenance, or
   * `XERO_NO_TAX_TYPE` where neither record supplied one) — every consumer
   * below must render exactly what it rendered before the field existed. See
   * `refusalOriginOf`.
   */
  const [refusal, setRefusal] = useState<{
    code: string;
    kind: XeroQuoteRefusal;
    origin: XeroTaxRefusalOrigin | null;
  } | null>(null);
  const [confirm, setConfirm] = useState<OpenConfirm | null>(null);
  const [collapsed, toggleCollapsed] = useXeroPanelCollapsed();

  const uid = useId();
  const bodyId = `${uid}-body`;

  /*
   * `pending` is state and so lags a rapid second click by a render; a ref is
   * read synchronously. Pushing twice queues the record twice, and confirming a
   * conflict twice can overwrite the side the operator just chose to keep.
   */
  const inFlight = useRef(false);

  const meta = XERO_ENTITIES[entity];
  const linked = isLinked(link);
  const tenantLine = connection.connected ? connection.tenantName : 'Not connected';
  const deepLink = xeroDeepLink(entity, link.id, connection.shortCode);

  /*
   * The one place the entity is named rather than looked up. Quotes and
   * invoices also carry `false` capabilities, but a `false` means different
   * things across the registry (deliberate design for `quotes.sync`, a hold
   * for invoices) — only for contacts does it mean Xero issues no id for a
   * person AT ALL, so there is nothing to show or act on, ever. No registry
   * flag distinguishes "later" from "never", so this stays a string check.
   * The panel still renders: "is this person in Xero?" deserves a sentence.
   */
  const structural = entity === 'contacts';

  /* Nothing about a contact can be unresolved: there is no link to break. */
  const unresolved = structural ? null : unresolvedLine(link);

  /*
   * `archived` covers two situations, separated only by the error code — see
   * `isStoodDownAsGone`. Both mean "do not write to this", so no button
   * changes; the SENTENCE changes, because "the record in Xero is archived"
   * sends somebody to Xero to look for a document that was deleted there.
   * The link keeps its dead Xero id, so `isLinked` is true, Create is
   * correctly withheld, and Unlink-then-recreate is the recovery.
   */
  const goneInXero = !structural && isStoodDownAsGone(link);
  /*
   * Only `XERO_ARCHIVED` may be told to un-archive — see `isRestorableInXero`.
   * Anything else `archived` and not gone gets the fact and no recovery,
   * because naming the wrong recovery is the defect this split prevents.
   */
  const restorableInXero = !structural && isRestorableInXero(link);

  async function run(action: PendingAction, work: () => Promise<unknown>) {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(action);
    setError(null);
    setConflict(null);
    setRefusal(null);
    try {
      await work();
      setConfirm(null);
      onChanged();
    } catch (err) {
      /*
       * Shown as a banner, never as per-field errors: a 403 (wrong role) and a
       * 409 (the Xero record moved under us) both carry no `details`, and
       * Xero's own field-level refusals arrive as a flat list on `link.error`
       * rather than the `{path, message}` pairs our own 422s use.
       *
       * `message` is folded in as a candidate line rather than a fallback for
       * an empty list: Xero's real objection lives in `details.xeroMessages`,
       * whose first entry is Xero's generic wrapper, while `message` now
       * carries the MOST SPECIFIC line. `xeroErrorLines` de-duplicates and
       * drops the generic wrapper when a specific line survives, so passing
       * both is safe either way. An error that never touched Xero carries no
       * `xeroMessages` and comes through as the server's own line verbatim —
       * e.g. the existing GUID named on `XERO_ALREADY_LINKED`.
       *
       * Switched on `error.code`, never on message text — production flattens
       * messages. See `XeroCreateConflict` and `XERO_QUOTE_REFUSALS` for what
       * each code means and why none is ever retried. `quoteRefusalOf` runs on
       * every failure for every entity rather than gating on
       * `entity === 'quotes'`: the map is keyed by code, a company's two 409
       * codes aren't in it and answer `null`, so a fifth entity issuing routed
       * refusals later costs nothing here.
       */
      if (err instanceof ApiError) {
        setError(xeroErrorLines([err.message, ...err.xeroMessages], err.message));
        /*
         * The company-approval gate is asked FIRST, and the order is
         * load-bearing in one direction only: all three codes are 409s, so on a
         * `create` they would otherwise reach the `createConflictOf` branch
         * below and be classified as `'other'` — a banner offering neither the
         * conflict's actions nor a destination. `quoteRefusalOf` cannot collide
         * with them (the codes are not in its map, and must not be), so this
         * sits above both rather than between them.
         *
         * ⚠️ `xero-push` is a THIRD gated action beside quote and invoice
         * creation — `xero-sync.service.ts` calls the guard — so the check is
         * not gated on the entity or on which button was pressed. It answers
         * `null` for every other failure, exactly as `quoteRefusalOf` does.
         *
         * Provenance needs no special case: the gate's 409 carries NO `details`
         * at all — deliberately, because half its call sites reach it with a
         * company the caller never sent — so `refusalOriginOf` below answers
         * `null` on its own. That absence is also why this is a PANEL-level
         * banner and can never be a field error.
         */
        const gate = billingGateRefusalOf(err);
        const routed = gate !== null ? BILLING_GATE_REFUSAL : quoteRefusalOf(err);
        if (routed !== null) {
          /*
           * Provenance is read on every routed refusal, not gated to the four
           * tax codes, for the same reason: it answers `null` for anything
           * that doesn't carry it, so a code taught later costs nothing here.
           */
          setRefusal({ code: err.code, kind: routed, origin: refusalOriginOf(err) });
        } else if (err.status === 409 && action === 'create') {
          setConflict(createConflictOf(err.code));
        }
      } else {
        setError(['The request didn’t complete.']);
      }
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  }

  function openConfirm(which: OpenConfirm) {
    /*
     * A stale failure from the previous attempt must not greet the next one —
     * including the conflict that offered this confirmation in the first place,
     * since cancelling out of it leaves the panel back at "Create in Xero" and
     * one click reproduces the same refusal, message and all. (That button is
     * now offered for COMPANIES only — see `CREATED_BY_SAVING` — and this is
     * still the panel it returns to.)
     */
    setError(null);
    setConflict(null);
    setRefusal(null);
    setConfirm(which);
  }

  function closeConfirm() {
    if (inFlight.current) return;
    setError(null);
    setRefusal(null);
    setConfirm(null);
  }

  const busy = pending !== null;
  /* Writes need both halves: the role, and a tenant to write to. */
  const canAct = canManage && connection.connected && !structural;

  /*
   * `canPush` and `canRetry` both answer true for a `failed` link — a retry IS
   * a push — but two buttons doing one thing invites confusion, so Retry wins
   * the label because it names what actually happened.
   *
   * ⚠️ A retry needs the `sync` capability too, and `canRetry` alone doesn't
   * check it: quotes are created inline, and the backend's worker refuses
   * quote jobs, so `sync` is false while `create` is true. Without the
   * `meta.capabilities.sync` check, a failed quote would offer a Retry that
   * calls `/xero/sync/quotes/{id}` and does nothing.
   */
  const createdBySaving = CREATED_BY_SAVING[entity];
  /*
   * ⚠️ `!createdBySaving` added 2026-08-31 and it is NOT redundant with
   * `meta.capabilities.sync`, though today the two agree. The capability says
   * which endpoint serves a push; this says whether a person is offered one at
   * all. See the block on `CREATED_BY_SAVING` — and the gap it leaves, which is
   * that a quote or invoice whose push failed has no manual retry left.
   */
  const showRetry =
    canAct && !createdBySaving && meta.capabilities.sync && canRetry(link) && link.id !== null;
  /*
   * ⚠️ The button label and the request come from ONE call. `POST
   * /xero/create/{entity}/{id}` is create-or-update, so "which of the two am I
   * about to do" must not be asked twice — offering "Send to Xero" on a record
   * with no Xero id used to call `/xero/sync` and change nothing. `pushIntentFor`
   * answers it from the link (the same thing the server branches on), and its
   * result is the token `api.pushToXero` demands, so the button can never be
   * labelled "Create" over a request that updates. `canCreateInXero` runs
   * inside it and still stops a linked record being created twice.
   *
   * There is deliberately no Update button: a linked quote is re-sent by the
   * save that changed it (`QuoteFormModal`/`QuoteDetailPage`), and a second,
   * manual trigger for the same write would report differently on the same
   * action.
   *
   * ⚠️ **CORRECTED 2026-08-30 — AND NOW THERE IS NO CREATE BUTTON ON A QUOTE
   * OR AN INVOICE EITHER.** The paragraph above ended:
   *
   * > *"`canCreateInXero` runs inside it and still stops a linked record being
   * > created twice."*
   *
   * Still true, and no longer the whole gate: `CREATED_BY_SAVING` withholds the
   * button for the two entities the server now creates on save. The reasoning
   * above is kept because it still governs the COMPANY button, which is the
   * only one left — and because the argument it makes ("a second, manual
   * trigger for the same write would report differently on the same action")
   * is exactly the argument that has now taken the create half too.
   */
  const pushIntent = pushIntentFor(entity, link);
  const createIntent = pushIntent !== null && pushIntent.action === 'create' ? pushIntent : null;
  const showCreate = canAct && createIntent !== null && !createdBySaving;
  /* Same gate, same reason as `showRetry` above: the decision, not the capability. */
  const showPush =
    canAct && !createdBySaving && canPush(entity, link) && !showRetry && !showCreate;
  const showLink = canAct && canLink(entity, link) && onRequestLink !== undefined;
  const showUnlink = canAct && linked;
  const showConflict = canAct && link.status === 'conflict';

  const hasActions = showCreate || showRetry || showPush || showLink || showUnlink || showConflict;

  /*
   * A Xero login can grant several organisations, and pushing SEED's
   * records into the wrong one is silent and undone only by deleting rows
   * inside an accounting system — the tenant is never left implicit next
   * to a button that writes to it. Folded, this row IS the panel, so it
   * also carries the status badge and whatever `unresolvedLine` says.
   *
   * Built ONCE and rendered by both modes: the default mode hands it to
   * `PanelHeader` as its description, the embedded mode draws it as a line of
   * its own under the toolbar. One source, so the two cannot word it apart.
   */
  /*
   * EMBEDDED MODE NEVER FOLDS ITSELF. The host section already has a fold, and
   * a Show/Hide inside it was a fold inside a fold — measured on the rebuilt
   * company page, where the section opened onto a second "Show" button. The
   * stored `collapsed` preference is read (the hook must run every render) and
   * then ignored, so standalone mounts keep remembering their own state.
   */
  const folded = !embedded && collapsed;

  const headerDescription =
    folded && unresolved !== null ? (
      <>
        {tenantLine} ·{' '}
        <span
          className={
            link.status === 'failed' ? 'font-semibold text-danger' : 'font-semibold text-warning'
          }
        >
          {unresolved}
        </span>
      </>
    ) : (
      tenantLine
    );

  /* The header's controls — the same nodes in both modes, gated identically. */
  const headerActions = (
    <>
      {folded && <XeroStatusBadge link={link} />}
      <Button
        size="sm"
        variant="ghost"
        leadingIcon={<ChevronIcon open={!folded} />}
        aria-expanded={!folded}
        aria-controls={bodyId}
        onClick={toggleCollapsed}
      >
        {folded ? 'Show' : 'Hide'}
        <span className="sr-only"> Xero details</span>
      </Button>
    </>
  );

  /*
   * `hidden` rather than a class: the fold takes the whole body out of
   * the tab order and off the accessibility tree, so a collapsed panel
   * cannot be tabbed into and its buttons cannot be reached blind. The
   * dialogs below stay outside it — they portal, and nothing can open one
   * while the panel is shut.
   */
  const body = (
      <div id={bodyId} hidden={folded}>
        <PanelBody className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <XeroStatusBadge link={link} />
          </div>

          <p className="m-0 text-sm text-fg-secondary [overflow-wrap:anywhere]">
            {structural
              ? CONTACTS_ARE_NOT_XERO_CONTACTS
              : goneInXero
                ? `The ${meta.xeroName.toLowerCase()} this was linked to no longer exists in Xero. The link is kept so nothing is written to it by mistake.`
                : statusDetail(link.status)}
          </p>

          {/*
           * ⛔ **TWO SENTENCES WERE REMOVED HERE — owner's helper-text ruling,
           * 2026-09-05 — and both are quoted so neither reads as an oversight.**
           *
           * *SUPERSEDED:* `This {meta.label} maps to a Xero {meta.xeroName}.`,
           * which sat beside the status badge above. Its note read *""Company →
           * Xero Contact" surprises everyone once. Say it in place."* Still true;
           * it is simply not one of the five kinds of sentence that may stand
           * next to a control any more.
           *
           * *SUPERSEDED:* `This {meta.label} goes to Xero when you save it.`,
           * gated on `createdBySaving`, which replaced a removed "Create in
           * Xero" button so the panel would not read as broken.
           *
           * ⚠️ **The FACTS are untouched and neither is now unsaid where it
           * matters.** `CONTACTS_ARE_NOT_XERO_CONTACTS` above still carries the
           * structural case, `statusDetail` still names the link's state, and
           * "Changes are not reaching Xero" below still fires with its four
           * per-status recoveries. **Do not restore either as an oversight** —
           * and in particular do not restore the second by re-adding a "Create
           * in Xero" button for an entity created by saving.
           */}
          {/*
           * The consequence, not just the state: both statuses are skipped by
           * the push queue, so the record keeps saving normally and quietly
           * stops reaching Xero. "There is a conflict" doesn't make anyone
           * act; "your edits are not reaching Xero" does.
           *
           * ⚠️ **CORRECTED 2026-08-30 — the exception is now a STRONGER claim,
           * not a weaker one.** The last sentence read:
           *
           * > *"This is framed as an exception because a save on a linked quote
           * > re-sends it (`QuoteFormModal`'s `onXeroAutoPush`), so these two
           * > statuses are the only ones where a saved edit stops here."*
           *
           * That prop is `onXeroPush` now and it pushes nothing — it hands the
           * server's report up. **The re-send happens on the SERVER, inside
           * the save**, so it no longer depends on which screen the edit came
           * from: the form modal, the inline header fields, a line-item write,
           * an importer and any second client all re-send a linked record.
           *
           * So the framing holds and holds wider: a saved edit reaches Xero by
           * default, and `conflict` and `archived` are the two statuses where
           * it stops here. ⚠️ **Do not narrow this copy back to one screen or
           * one document type** — this panel serves companies, quotes and
           * invoices, and naming a quote form was already the narrower half of
           * a true statement.
           */}
          {!structural && isSilentlyNotSyncing(link) && (
            <Notice
              tone="warning"
              role="status"
              className="[overflow-wrap:anywhere]"
              title="Changes are not reaching Xero."
            >
              {link.status === 'conflict'
                ? /* Only ONE of the two buttons below restores sending: `keep: 'emerge'` re-pushes; `keep: 'xero'` discards what was saved here. Never claim resolving restores sending in general. */
                  `Saved here, not sent. Choose below which version stands.`
                : goneInXero
                  ? /* ⚠️ The recovery differs now that only a COMPANY has a create button: for a quote or an invoice, unlinking is followed by saving it, not by pressing anything. Naming the wrong one sends somebody hunting a button that is not there. */
                    `Saved here, not sent. Unlink below, then ${createdBySaving ? 'save it again' : 'create it in Xero again'}.`
                  : restorableInXero
                    ? /* States only what un-archiving restores (the ability to write) — this panel is generic and cannot promise a mechanism that doesn't exist for every entity. */
                      `Saved here, not sent. Un-archive the record in Xero to write to it again.`
                    : /* Unrecognised case: state the fact, name NO recovery. "Un-archive it in Xero" belongs only to XERO_ARCHIVED. */
                      `Saved here, not sent. Xero will not accept writes to this record.`}
            </Notice>
          )}

          {/* No id row or timestamps for a record that can never have either — permanent em dashes would read as missing data. */}
          {!structural && (
            <dl className="m-0 grid gap-x-6 gap-y-3 sm:grid-cols-2">
              {linked && (
                <div className="min-w-0 sm:col-span-2">
                  <Term>Xero id</Term>
                  <dd className="m-0 mt-1 font-mono text-xs text-fg-secondary [overflow-wrap:anywhere]">
                    {link.id}
                  </dd>
                </div>
              )}
              <div className="min-w-0">
                <Term>Last sent to Xero</Term>
                <Value>{dateTimeOrDash(link.lastSyncedAt)}</Value>
              </div>
              <div className="min-w-0">
                <Term>Last changed in Xero</Term>
                <Value>{dateTimeOrDash(link.updatedAtXero)}</Value>
              </div>
            </dl>
          )}

          {/* Only rendered when a deep link can be built — `shortCode` can be absent and a ContactPerson has no page at all; `xeroDeepLink` returns null rather than link to the wrong organisation or nowhere. */}
          {deepLink && (
            <p className="m-0">
              <ExternalTextLink
                href={deepLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium"
              >
                Open in Xero
                <ExternalIcon />
                <span className="sr-only">(opens in a new tab)</span>
              </ExternalTextLink>
            </p>
          )}

          {link.error && (
            <Notice
              tone="danger"
              role="alert"
              title={
                link.status === 'conflict'
                  ? 'Xero reported a conflict.'
                  : goneInXero
                    ? /* Nothing was refused — the document was deleted there. */
                      'The document this was linked to was deleted in Xero.'
                    : 'Xero refused the last send.'
              }
            >
              <div className="flex flex-col items-start gap-2">
              {/*
               * ⚠️ `message` is `string | null`. Rendering `null` straight into
               * JSX (or `?? ''`) prints an empty line that looks like a layout
               * bug — so the null case gets its own explicit sentence instead.
               */}
              {link.error.message === null ? (
                <p className="m-0 min-w-0 [overflow-wrap:anywhere] text-fg-secondary">
                  No reason was recorded against it.
                </p>
              ) : (
                <p className="m-0 min-w-0 [overflow-wrap:anywhere]">{link.error.message}</p>
              )}
              {/*
               * Rendered from a real field, never scraped out of `message`.
               *
               * ⚠️ The two stamps read "backwards" on purpose: the baseline is
               * held at the last agreed state rather than advanced to what
               * Xero now has, or the next push would find no drift and
               * overwrite the very change this is reporting.
               */}
              {link.error.observedAtXero && (
                <p className="m-0 min-w-0 [overflow-wrap:anywhere] text-fg-secondary">
                  Changed in Xero {dateTimeOrDash(link.error.observedAtXero)}; last agreed copy{' '}
                  {dateTimeOrDash(link.updatedAtXero)}.
                </p>
              )}
              {/* Xero returns its refusals per element, not per field path — a flat list, unlike our own 422 `details`. */}
              {link.error.details && link.error.details.length > 0 && (
                <ul className="m-0 list-disc space-y-0.5 pl-4">
                  {link.error.details.map((detail) => (
                    <li key={detail} className="min-w-0 [overflow-wrap:anywhere]">
                      {detail}
                    </li>
                  ))}
                </ul>
              )}
              </div>
            </Notice>
          )}

          {/*
           * A refusal this app has been TAUGHT renders by DESTINATION, not as
           * one more "couldn't send to Xero" — the quote codes exist precisely
           * because they send the reader to different screens. Anything
           * unrecognised (the two create conflicts, every connection-level
           * code, anything the backend adds later) falls to the generic banner
           * below, unchanged. Count `XERO_QUOTE_REFUSALS` for how many exist;
           * never write the number here.
           *
           * ⚠️ Two matchers feed this, not one: `XERO_QUOTE_REFUSALS` by way of
           * `quoteRefusalOf`, and the billing gate by way of
           * `billingGateRefusalOf` — see `BILLING_GATE_REFUSAL`. So "a routed
           * refusal" is no longer the same set as "a code in the Xero map", and
           * a count taken from either alone describes half of it.
           */}
          {error && confirm === null && refusal !== null && (
            <RefusalBanner
              code={refusal.code}
              refusal={refusal.kind}
              origin={refusal.origin}
              lines={error}
              company={company ?? null}
              /* This panel is mounted on both /quotes/:id and /invoices/:id, and the seven shared refusal codes route through the same copy — without `entity`, the invoice page would say "The quote is fine." */
              entity={entity}
              /* The dialog edits the COMPANY, so it's offered only to someone who may write; the link beside it is a read and is never gated. */
              onFixCompanyDefaults={canManage ? onFixCompanyDefaults : undefined}
            />
          )}

          {/* A failure inside a confirmation stays inside it, so the dialog can be retried. */}
          {error && confirm === null && refusal === null && (
            <Notice tone="danger" role="alert">
              <div className="flex flex-col items-start gap-2">
              {error.map((line) => (
                <p key={line} className="m-0 min-w-0 [overflow-wrap:anywhere]">
                  {line}
                </p>
              ))}
              {/*
               * ⚠️ No branch below offers a RETRY — both conflicts are
               * terminal. A retry beside "name taken" invites the one action
               * that cannot work, and once failed twice, invites editing the
               * name until it goes through — which is how a duplicate
               * customer appears in the accounting system. Nothing on our
               * side can detect or undo that.
               */}
              {/*
               * Already linked: the picker is NOT offered — it would be a
               * dead end. The server's message names the GUID the record
               * already points at; detaching it is the only move. The
               * footer's Unlink isn't available here (this record reads as
               * unlinked on our side, which is why Create was offered), so
               * the banner carries the action itself.
               */}
              {conflict === 'already-linked' && canAct && (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={busy}
                  onClick={() => openConfirm('unlink')}
                >
                  Unlink
                </Button>
              )}
              {/* Name taken, or an unrecognised 409: the picker opens pre-searched on this record's name — see `onRequestLink`. */}
              {(conflict === 'name-taken' || conflict === 'other') &&
                onRequestLink !== undefined && (
                  <Button size="sm" disabled={busy} onClick={onRequestLink}>
                    Link to existing…
                  </Button>
                )}
              </div>
            </Notice>
          )}
        </PanelBody>

        {/*
         * ⛔ **THE DISCONNECTED FOOTER WAS REMOVED — owner's helper-text ruling,
         * 2026-09-05.** Gated on `!structural && !connection.connected`, it read
         * `Xero is not connected.` followed by a link to `/integrations` reading
         * "Set up the connection".
         *
         * ⚠️ **The panel is still RENDERED while Xero is disconnected, and that
         * is the part that matters and is unchanged.** A stored id is still the
         * right id, and hiding this panel is how a record gets linked twice after
         * reconnecting — so the id, the timestamps and the status badge all still
         * show. What went is the sentence saying WHY the actions are absent, and
         * the only route to the fix.
         *
         * ⚠️ Same sentence and same link were removed from
         * `XeroSalesDefaultsFields` in the same pass; they are the two places
         * that named the disconnected state, and neither does now.
         */}

        {hasActions && (
          <PanelFooter>
            {/* `flex-wrap`, because Button is `shrink-0` and three of these do not fit at 320px. */}
            <div className="flex flex-wrap items-center gap-2">
              {showCreate && createIntent !== null && (
                <Button
                  variant="primary"
                  loading={pending === 'create'}
                  disabled={busy}
                  onClick={() =>
                    void run('create', () => api.pushToXero(entity, recordId, createIntent))
                  }
                >
                  Create in Xero
                </Button>
              )}
              {showPush && (
                <Button
                  variant="primary"
                  loading={pending === 'push'}
                  disabled={busy}
                  onClick={() => void run('push', () => api.pushXero(entity, recordId))}
                >
                  Send to Xero
                </Button>
              )}
              {showRetry && (
                <Button
                  variant="primary"
                  loading={pending === 'push'}
                  disabled={busy}
                  onClick={() => void run('push', () => api.pushXero(entity, recordId))}
                >
                  Retry
                </Button>
              )}
              {showLink && (
                <Button disabled={busy} onClick={onRequestLink}>
                  Link to existing…
                </Button>
              )}
              {showConflict && (
                <>
                  <Button disabled={busy} onClick={() => openConfirm('keep-emerge')}>
                    Keep SEED EMERGE version
                  </Button>
                  <Button disabled={busy} onClick={() => openConfirm('keep-xero')}>
                    Keep Xero version
                  </Button>
                </>
              )}
              {showUnlink && (
                <Button variant="danger" disabled={busy} onClick={() => openConfirm('unlink')}>
                  Unlink
                </Button>
              )}
            </div>
          </PanelFooter>
        )}
      </div>
  );

  const dialogs = (
    <>
      <ConfirmDialog
        open={confirm === 'unlink'}
        onClose={closeConfirm}
        onConfirm={() => void run('unlink', () => api.unlinkXero(entity, recordId))}
        title="Unlink from Xero?"
        description={
          <>
            <strong className="font-semibold text-fg">{recordName}</strong> is detached in SEED EMERGE
            only. The record in Xero is left as it is.
          </>
        }
        confirmLabel="Unlink"
        loading={pending === 'unlink'}
        error={error}
      />

      {/* Both halves of a conflict are destructive and irreversible, so each names the side that loses. No "merge": the two sides hold different field sets, and reconciling field by field across an accounting boundary is how a filed figure changes silently. */}
      <ConfirmDialog
        open={confirm === 'keep-emerge'}
        onClose={closeConfirm}
        onConfirm={() =>
          void run('keep-emerge', () =>
            api.resolveXeroConflict(entity, recordId, { keep: 'emerge' }),
          )
        }
        title="Overwrite the Xero version?"
        description={
          <>
            The SEED EMERGE version of{' '}
            <strong className="font-semibold text-fg">{recordName}</strong> replaces what is in
            Xero. Those changes cannot be recovered.
          </>
        }
        /*
         * ⚠️ NOT "Send to Xero" — that phrase names `POST /xero/sync/{entity}/{id}`
         * everywhere else in this app, which is `capabilities.sync` (false for
         * quotes and invoices, answering `409 XERO_PUSH_IS_INLINE`). This
         * button calls `POST /xero/conflict/…` instead, so reusing the label
         * would name a different endpoint than the one it calls. Naming the
         * OUTCOME is true for every entity and matches the title above.
         */
        confirmLabel="Overwrite in Xero"
        loading={pending === 'keep-emerge'}
        error={error}
      />

      <ConfirmDialog
        open={confirm === 'keep-xero'}
        onClose={closeConfirm}
        onConfirm={() =>
          void run('keep-xero', () => api.resolveXeroConflict(entity, recordId, { keep: 'xero' }))
        }
        title="Overwrite the SEED EMERGE version?"
        description={
          <>
            The Xero version replaces{' '}
            <strong className="font-semibold text-fg">{recordName}</strong> in SEED EMERGE, discarding
            edits made here.
          </>
        }
        confirmLabel="Keep Xero version"
        loading={pending === 'keep-xero'}
        error={error}
      />
    </>
  );

  if (embedded) {
    /*
     * No Panel, no PanelHeader, and NO Show/Hide: the host draws the border,
     * the heading and the fold (see `folded`). What survives is the tenant
     * line, as its own line, for the reason given on the prop — the body under
     * it is always open here, so the status badge and the unresolved sentence
     * render in the body rather than being lifted into a header.
     */
    return (
      <>
        <p className="m-0 px-4 pt-3 text-xs text-fg-muted">{headerDescription}</p>
        {body}
        {dialogs}
      </>
    );
  }

  return (
    <Panel aria-labelledby="xero-panel-title">
      <PanelHeader
        id="xero-panel-title"
        title="Xero"
        description={headerDescription}
        /* Nothing follows a folded header, so its rule would hang under the panel. */
        className={collapsed ? 'border-b-0' : undefined}
        actions={headerActions}
      />
      {body}
      {dialogs}
    </Panel>
  );
}

/* -------------------------------------------------------------------------
 * Rendering a refusal by where it sends the reader
 * ---------------------------------------------------------------------- */

/**
 * The two codes a company's Sales-defaults dialog can actually fix, and the
 * only ones `onFixCompanyDefaults` is offered for.
 *
 * Named apart from the destination because "lives on the company record" and
 * "this app has a dialog for it" are different claims: `XERO_COMPANY_NOT_LINKED`
 * points at the same company but isn't a defaults problem, and the dialog
 * would open a form holding no field that could help.
 *
 * ⚠️ This set is the WHOLE gate — do not derive it from `destination` (e.g.
 * `destination === 'company-record' && SALES_DEFAULT_CODES.has(code)`).
 * `XERO_NO_LINE_AMOUNT_TYPE` routes through `quote-form-or-company`, not
 * `company-record`, so a destination-based gate silently drops the button on
 * the one code the dialog was built for.
 *
 * The dialog is safe here because `XERO_NO_LINE_AMOUNT_TYPE`'s backend
 * resolver (`resolveAmountType`) refuses only when the company's own default
 * is **absent** — there's no correct value to overwrite, unlike
 * `quote-lines-or-company` below.
 *
 * ⚠️ `XERO_TAX_TYPE_NOT_REVENUE` and `XERO_TAX_TYPE_UNKNOWN` must stay OUT of
 * this set. The dialog writes the COMPANY's Sales defaults, but either code
 * can be raised by a tax code stored on a LINE ITEM instead (measured: a
 * refusal on a line's `INPUT2` while the company's own default was a
 * perfectly good `ZERORATEDOUTPUT`). Offering the dialog there risks
 * overwriting a correct company default in response to a refusal it didn't
 * cause. When the response carries `source: 'company'` with no `lineId`,
 * `RefusalBanner` offers the dialog on that evidence instead — never by
 * widening this set, which would offer it on line-sourced refusals too.
 */
const SALES_DEFAULT_CODES: ReadonlySet<string> = new Set([
  'XERO_NO_TAX_TYPE',
  'XERO_NO_LINE_AMOUNT_TYPE',
]);

/* -------------------------------------------------------------------------
 * The facts every sentence below is allowed to name
 *
 * ⚠️ `noun` exists because `XeroPanel` is mounted by `InvoiceDetailPage` as
 * well as `QuoteDetailPage`, and {@link quoteRefusalOf} is asked on EVERY
 * failure for EVERY entity — the seven codes both pushes share route through
 * here on an invoice too, and without a threaded noun they'd render "The
 * quote is fine…" on the invoice page.
 *
 * The noun has exactly one source: `XERO_ENTITIES[entity].label`. Never write
 * a second literal below, and never let a caller pass a noun of its own.
 *
 * ⚠️ No sentence below forms a POSSESSIVE or a PLURAL from it. `label + "'s"`
 * happens to read correctly for all four entities today (none ends in `s`),
 * which is exactly what makes it a trap — it would ship, read fine, and break
 * for the first label that does. Write "the line items on this {noun}", never
 * "this {noun}'s line items", so no sentence needs inflection.
 * {@link XeroEntityMeta.plural} is Title Case and unrelated to this rule.
 * ---------------------------------------------------------------------- */
interface RefusalCopy {
  /** What this record IS, singular and lower case, straight from the registry — reads correctly after "this" and "the" in every slot below. */
  noun: string;
  /**
   * The company this record bills, named where known and referred to
   * obliquely where not — see `companyLabel` in {@link RefusalBanner}. No
   * slot may assume a real name.
   */
  company: string;
  /** Whether the record is already in Xero. Changes the closing instruction only. */
  alreadyInXero: boolean;
}

/**
 * One shape for every string in this section — heading, explanation and
 * "where do I fix this" alike. Deliberately uniform (even though a heading
 * never names the company) so a slot always fills from a function with the
 * same signature, and adding a fact later is one field, not four signatures.
 */
type RefusalCopyFn = (copy: RefusalCopy) => string;

/**
 * The headline for a DESTINATION — the fallback, overridden per code by
 * {@link REFUSAL_HEADINGS} where one code knows something its neighbours don't.
 * Resolution order is `source ?? code ?? destination`. The first line always
 * answers "whose problem is this" before any detail: `company-record` never
 * opens by saying the document is fine, because the reader is looking at the
 * document and every instinct is to edit it, which cannot help.
 *
 * ⚠️ `quote-lines-or-company` must NOT answer "whose problem is this" — the
 * response doesn't say. The two tax-code refusals can fire from a LINE item
 * while the company's default is correct, so this heading names the VALUE
 * that refused, not a record, and the paragraph below names both places it
 * can come from.
 *
 * ⚠️ `quote-form-or-company` must not borrow either of the above. It cannot
 * say the document is fine (it's one of the two records missing the
 * setting), and cannot say Xero refused anything (nothing was sent). It
 * states only what's true of both records, and the paragraph below names
 * each control in its own words — a company stores the CONTACT vocabulary
 * (`NONE`) and a document the DOCUMENT one (`NOTAX`) for what both pickers
 * label "No tax", so "set the same thing in either place" would be wrong.
 */
const DESTINATION_HEADINGS: Readonly<Record<XeroRefusalDestination, RefusalCopyFn>> = {
  'quote-form': ({ noun }) => `Xero needs something this ${noun} does not carry.`,
  'company-record': ({ noun }) => `The ${noun} is fine. The customer record is not ready.`,
  'quote-form-or-company': ({ noun }) =>
    `Neither this ${noun} nor the customer record carries a setting Xero needs.`,
  'quote-lines-or-company': ({ noun }) => `Xero refused the tax code on this ${noun}.`,
  /* Neither of the last two names the record at all, so neither takes the noun. */
  'xero-settings': () => 'This can only be changed inside Xero.',
  nowhere: () => 'This cannot be sent to Xero.',
};

/**
 * A headline for ONE code, where the destination's own is false for it.
 *
 * Per-code copy exists rather than a further destination because two
 * independent axes (which control, and whether a value exists-but-refused vs
 * absent) multiply: a destination per combination needs a new one for every
 * new code, where per-code copy carries only what that code adds.
 *
 * `XERO_NO_TAX_TYPE` could not reuse `quote-lines-or-company`'s copy: that
 * destination says Xero *refused* a code and *doesn't say which one applied*
 * — both false when both places are simply empty. Its wording is about
 * ambiguity; this code's problem is absence. The heading below states the
 * two absences and nothing more — it does not claim the document is fine
 * (its lines carry no tax code either, and the line grid is a real fix).
 */
/**
 * Shared by all three billing-gate codes — see the entries in
 * {@link REFUSAL_HEADINGS} for why they take one heading between them.
 *
 * Declared ABOVE the map, not beside its entries: the map is built at module
 * load, so a `const` below it would still be in its temporal dead zone when the
 * entries are evaluated.
 *
 * Takes no {@link RefusalCopy} because it names no record type. That is the
 * point rather than an omission — this panel is mounted on the company detail
 * page as well, where every noun-bearing phrasing describes the record the
 * reader is already looking at.
 */
const billingGateHeading: RefusalCopyFn = () => 'The customer record has not cleared approval.';

const REFUSAL_HEADINGS: ReadonlyMap<string, RefusalCopyFn> = new Map<string, RefusalCopyFn>([
  /* "on the lines of this {noun}", not "this {noun}'s lines" — see the {@link RefusalCopy} header: no sentence here inflects the noun. */
  [
    'XERO_NO_TAX_TYPE',
    ({ noun }) => `No Xero tax code on the lines of this ${noun}, and none on the customer.`,
  ],
  /*
   * The three billing-gate codes share ONE heading, and they need one because
   * `company-record`'s own is false for them twice over:
   *
   *  - it opens "The {noun} is fine", and this panel is mounted on the COMPANY
   *    detail page too (`xero-push` is gated as well as quote and invoice
   *    creation), where that reads "The company is fine. The customer record is
   *    not ready." — a sentence contradicting itself;
   *  - "not ready" describes a record still being filled in, which is not what
   *    any of the three says.
   *
   * ⚠️ It says the approval has not CLEARED and stops there, deliberately.
   * Awaiting a decision, refused, and unreadable send the reader to the same
   * place and are three different facts; the server's message is the one thing
   * that can tell them apart and it is rendered directly below this line. A
   * heading naming one of them would be right on a third of the refusals and
   * confidently wrong on the rest — and "not approved yet" over a REJECTED
   * company leaves somebody waiting for an email that is never coming.
   *
   * ⚠️ Keyed off the exported constants, never off the strings: the codes are
   * `@/lib/company-approval`'s to spell, and a copy of them written out here is
   * how the panel and the matcher come to disagree about which failures route.
   */
  [COMPANY_NOT_APPROVED_CODE, billingGateHeading],
  [COMPANY_APPROVAL_REJECTED_CODE, billingGateHeading],
  [COMPANY_APPROVAL_UNREADABLE_CODE, billingGateHeading],
]);

/**
 * The closing instruction on a fixable refusal — the ONE clause that differs
 * between the create path and the automatic-update path. Kept as a function
 * so the two paths cannot drift into two different promises about the same
 * mechanism. Not exported: a second caller should take the whole explanation,
 * not this fragment.
 */
function nextStep({ noun, alreadyInXero }: RefusalCopy): string {
  return alreadyInXero
    ? `save again — a saved ${noun} is sent to Xero on its own once it is linked.`
    : 'create it in Xero.';
}

/**
 * Our own sentence for a code, where our own sentence loses nothing. Absent
 * for every `verbatim` code — there the server's message carries the half no
 * static copy can hold (which tax rate; which tracking option), and standing
 * in front of it would replace the useful part with a paraphrase.
 *
 * Functions rather than pre-evaluated templates, because the company is named
 * where known and referred to obliquely where not — every slot is written so
 * "the company this quote bills" reads correctly.
 *
 * ⚠️ These sentences END IN AN INSTRUCTION, and the instruction is wrong on
 * one of the two paths this banner serves. It was written for the CREATE
 * flow ("then create it in Xero"), but is also rendered for the automatic
 * push on an ALREADY-LINKED quote — where "create it in Xero" names a Create
 * button that is deliberately absent, on a document already sitting in the
 * accounts. The caller passes whether the record is already in Xero, read
 * from the link row (never inferred), and only the closing instruction
 * varies — the diagnosis half is identical because the fault is identical.
 */
const REFUSAL_EXPLANATIONS: ReadonlyMap<string, RefusalCopyFn> = new Map<string, RefusalCopyFn>([
  /*
   * ⚠️ These four `XERO_QUOTE_*` entries are quote-only codes — the invoice
   * push raises `XERO_INVOICE_NO_COMPANY` and friends instead, deliberately
   * NOT in `XERO_QUOTE_REFUSALS` (see `src/lib/xero.ts`), so they never reach
   * this map. They still thread the noun rather than hardcoding "quote", so
   * no literal here could be copied into a shared entry later.
   *
   * ⚠️ `XERO_QUOTE_NOT_DRAFT` names Xero's QUOTE lifecycle ("sent, accepted or
   * invoiced") — not an invoice's statuses. If `XERO_INVOICE_NOT_DRAFT` is
   * ever adopted here it needs its own sentence; swapping the noun alone
   * would produce a fluent lie.
   */
  [
    'XERO_QUOTE_NO_COMPANY',
    (copy) =>
      `No company on this ${copy.noun}. Add one with Edit above, then ${nextStep(copy)}`,
  ],
  [
    'XERO_QUOTE_NO_LINES',
    (copy) => `No line items. Add at least one line, then ${nextStep(copy)}`,
  ],
  /*
   * ⚠️ `XERO_QUOTE_NO_PROJECT` / `XERO_INVOICE_NO_PROJECT` are deleted
   * server-side and deliberately have no entry here — a document that can
   * derive no tracking now pushes untracked, since `Tracking` is optional in
   * Xero's API, so neither code can arrive. Do not add one back.
   *
   * ⚠️ This does NOT mean project copy is obsolete generally: a LINE whose
   * product is sold through projects and names none is still refused, still
   * routed to the line grid's Project control, in `src/lib/line-project.ts`.
   * Only the DOCUMENT-level tracking refusal is gone.
   */
  /*
   * ⚠️ "every future DOCUMENT", not "every future {noun}" — threading the
   * noun here would be wrong. A Xero contact is not per-document: linking it
   * once unblocks this customer's quotes and invoices together, so the blast
   * radius of the fix is a fact about the fix, not about the record on
   * screen. Same reasoning as `REFUSAL_PLACES` below.
   */
  [
    'XERO_COMPANY_NOT_LINKED',
    ({ company }) =>
      `No Xero contact for ${company}. Create or link one on the company record.`,
  ],
  [
    'XERO_NO_TAX_TYPE',
    ({ company, noun }) =>
      `No Sales tax rate on ${company}, and no line on this ${noun} sets one.`,
  ],
  /* Names BOTH empty records — the refusal fires only when the quote also doesn't say, and the quote form has the field, so naming only the company would send the reader past a control on the screen they're looking at. */
  [
    'XERO_NO_LINE_AMOUNT_TYPE',
    ({ company, noun }) =>
      `Neither ${company} nor this ${noun} says whether Sales amounts include tax.`,
  ],
  /* Describes the VALUE, not a record — the code can be stored on the company or on a line item (see `quote-lines-or-company` above), and the response doesn't say which. The destination paragraph names both places it could have come from. */
  [
    'XERO_TAX_TYPE_UNKNOWN',
    ({ noun }) =>
      `The Xero tax code on this ${noun} no longer exists in Xero. Pick one that is still live.`,
  ],
  [
    'XERO_QUOTE_NOT_DRAFT',
    ({ noun }) =>
      `The ${noun} in Xero has moved past draft. Change it on the document in Xero.`,
  ],
  /* Describes the two steps that actually exist, in order: the backend stands the link down to `archived` and keeps the dead Xero id, so Create is correctly withheld until the link is detached. Never name "a fresh create" as the immediate next step — that button isn't on screen yet. */
  [
    'XERO_QUOTE_GONE',
    ({ noun }) =>
      `The ${noun} was deleted in Xero. Unlink it below, then create it in Xero again.`,
  ],
]);

/**
 * WHERE to go, as prose. Same shape as every other string here — see
 * {@link RefusalCopyFn}. Kept as its own name because it's the slot
 * {@link placesCopyFor} resolves.
 */
type PlacesCopy = RefusalCopyFn;

/**
 * The "where do I fix this" paragraph for a DESTINATION.
 *
 * ⚠️ These live in data, not as JSX branches, because that's what makes the
 * per-code override in {@link REFUSAL_PLACES} safe: as separate
 * `destination === …` blocks, a code override could render ALONGSIDE its
 * destination's paragraph — two paragraphs sending one reader to two places.
 * One slot, resolved by {@link placesCopyFor}, makes that unexpressible.
 *
 * `Partial` on purpose: `quote-form`, `company-record` and `nowhere` have no
 * paragraph — the heading plus our own sentence already say where to go.
 */
const DESTINATION_PLACES: Readonly<Partial<Record<XeroRefusalDestination, PlacesCopy>>> = {
  /* Nothing in EMERGE can fix this — a link would send someone looking for a field that doesn't exist, with no way to tell the guidance was guessed. */
  'xero-settings': ({ noun }) =>
    `Nothing to correct in SEED EMERGE — the option has to be added in Xero before this ${noun} can be created.`,
  /*
   * TWO places, named in resolution order, NEITHER asserted. The push
   * resolves one tax code from two candidates (the line's own override
   * first, then the company's Sales default) and discards which one it
   * used, so the refusal can't say — the order is the only real guidance,
   * and it's useful: a line code beats the company default, so the line is
   * both the likelier culprit and the narrower fix.
   *
   * No inline Sales-defaults dialog on this branch — see `SALES_DEFAULT_CODES`.
   */
  'quote-lines-or-company': ({ company, noun }) =>
    `Check the Tax rate column on this ${noun}’s line items first, then the Sales defaults on ${company}.`,
  /*
   * TWO places, BOTH EMPTY — two working fixes, not two suspects. Unlike
   * `quote-lines-or-company` above (where a value exists and its source is
   * unknown), here NEITHER record supplied one, per the server's own
   * message, so both fixes are genuinely real.
   *
   * ⚠️ Only the COMPANY-side label is quoted; the document-side one is
   * described instead, deliberately: the two forms label the same
   * `xeroLineAmountType` enum differently (`QuoteFormModal` renders
   * `label="Tax"`, `InvoiceFormModal` renders `label="Line amounts Xero
   * should use"`), so a quoted label can't be threaded from the registry
   * without being wrong on one entity. The company side keeps its quote
   * because `XeroSalesDefaultsFields` is one shared component.
   *
   * "every future DOCUMENT", not "every future {noun}" — same reasoning as
   * `XERO_COMPANY_NOT_LINKED` above: a company Sales default isn't per-document.
   */
  'quote-form-or-company': ({ company, noun }) =>
    `Set either: “Line amounts Xero should default to” in ${company}’s Xero Sales defaults, or the Xero section under Edit on this ${noun}.`,
};

/**
 * The same paragraph for ONE code, where the destination's own is wrong for
 * it. See {@link REFUSAL_HEADINGS} for why this is a per-code override rather
 * than a seventh destination.
 *
 * ⚠️ Both fixes state their BLAST RADIUS because they aren't equivalent: the
 * company's Sales default covers every future document for that customer,
 * while a code typed on one line covers that line only — a second untaxed
 * line refuses again the moment the first is fixed. Naming only "set it on
 * the line" hides that a single company edit ends it for good.
 *
 * The quote-side control named here is the line grid's Tax rate column, NOT
 * the quote form's Tax field — two different controls over two different
 * Xero fields (see `XERO_NO_LINE_AMOUNT_TYPE`, which is the wrong one).
 */
const REFUSAL_PLACES: ReadonlyMap<string, PlacesCopy> = new Map<string, PlacesCopy>([
  [
    'XERO_NO_TAX_TYPE',
    ({ company, noun }) =>
      `Set either: “Sales tax rate Xero should default to” in ${company}’s Xero Sales defaults, or the Tax rate column on this ${noun}’s line items.`,
  ],
]);

/* -------------------------------------------------------------------------
 * When the server says WHICH record supplied the tax code
 *
 * ⚠️ These two functions REPLACE the both-places copy rather than joining it.
 * `source: 'line'` means the company's Sales defaults did not supply the
 * code, so "check the line items first, then the company" is a wrong
 * sentence sending someone to a record the response has just ruled out.
 *
 * The both-places copy is still reached for `source: null` (an older server,
 * or `XERO_NO_TAX_TYPE`, where NEITHER record supplied a code and both fixes
 * are genuinely real) — see `DESTINATION_PLACES['quote-lines-or-company']`
 * and `REFUSAL_PLACES['XERO_NO_TAX_TYPE']`.
 *
 * ⚠️ `source: 'company'` carrying a `lineId` does NOT settle where to go,
 * and since 2026-08-30 it does not settle it in EITHER direction. The contract
 * half is unchanged and still holds: on `XERO_TAX_RATE_MISMATCH` the backend
 * sends a `lineId` even when the code was inherited, because that refusal is
 * about the LINE's own `taxRate` disagreeing with the code's percentage. See
 * {@link refusalOriginOf} for the contract rules.
 *
 * ⚠️ *SUPERSEDED, the CONCLUSION and not the contract: "the line is the
 * subject whichever record supplied the code."* That was written while every
 * line's percentage was typeable, and it was read as "so the company is never
 * where the fix belongs". The percentage is now typeable only where the company
 * states NO rate: on `taxRateSource: 'company'` the box is READ-ONLY and the
 * figure is changed on the company record — `isCompanySourcedTaxRate` in
 * `src/lib/xero-tax-rates.ts`, and the three positions of 2026-08-30 are set
 * out in `QuoteLineItemsTable`'s module header.
 *
 * 🔴 **AND THE RESPONSE DOES NOT CARRY THE FIELD THAT DECIDES IT.**
 * `origin` is `source` / `lineId` / `productName` and nothing else, and
 * `source` is the provenance of the tax CODE, which is a different column from
 * the provenance of the RATE. **Never infer one from the other.** A company
 * holding an `xeroSalesTaxType` with a NULL `xeroSalesTaxRate` is the ordinary
 * live state — every live company held a null rate when migration 092 shipped,
 * read from `company-tax.lookup.ts` in the backend — and there
 * `source: 'company'` arrives beside a line whose percentage is fully editable.
 * The routing below is left exactly as it was for that reason;
 * `companyIsTheFix` records what would have to reach us before it can change.
 * ---------------------------------------------------------------------- */

/**
 * The heading, when the provenance is known. `undefined` for `source: null`, so
 * the code's own heading and then the destination's still decide.
 */
function sourceHeadingFor(origin: XeroTaxRefusalOrigin): RefusalCopyFn | undefined {
  const { source, lineId, productName } = origin;

  /*
   * “the … line on this {noun}”, never “this {noun}’s … line”. Both readings
   * are natural English and only one of them needs the noun inflected — see the
   * {@link RefusalCopy} header for why that decides it.
   */
  if (source === 'line') {
    return productName
      ? ({ noun }) => `The Xero tax code came from the “${productName}” line on this ${noun}.`
      : ({ noun }) => `The Xero tax code came from a line item on this ${noun}.`;
  }

  if (source === 'company') {
    if (lineId === null) {
      return ({ noun }) =>
        `The Xero tax code came from the customer record, not from this ${noun}.`;
    }
    /* Inherited from the company, but the server named the line it does not fit. */
    return productName
      ? ({ noun }) =>
          `The tax code came from the customer record and does not fit the “${productName}” line on this ${noun}.`
      : ({ noun }) =>
          `The tax code came from the customer record and does not fit a line on this ${noun}.`;
  }

  return undefined;
}

/**
 * The “where do I fix this” paragraph, when the provenance is known.
 *
 * Every branch states the BLAST RADIUS of the fix it names, for the same reason
 * {@link REFUSAL_PLACES} does: a code typed on one line clears that line only, so
 * someone told nothing else works through a quote one refusal at a time.
 */
function sourcePlacesFor(origin: XeroTaxRefusalOrigin): PlacesCopy | undefined {
  const { source, lineId, productName } = origin;
  const line = productName ? `the “${productName}” line` : 'that line';

  if (source === 'line') {
    return ({ company }) =>
      `Fix it in the Tax rate column on ${line}. The Sales defaults on ${company} are not involved.`;
  }

  if (source === 'company') {
    if (lineId === null) {
      return ({ company, noun }) =>
        `No line on this ${noun} sets a code, so fix the Sales defaults on ${company}.`;
    }
    return ({ company }) =>
      `Inherited from ${company}’s Sales defaults, but the refusal is about ${line} — start in its Tax rate column.`;
  }

  return undefined;
}

/**
 * The paragraph to render: the response's own where the server said which record
 * supplied the code, the code's next, the destination's otherwise, and nothing
 * at all where none of the three speaks.
 *
 * **Source beats code beats destination**, in that order, because that is
 * increasing specificity: `source` is a fact about THIS refusal, a code override
 * is a fact about every refusal of that code, and a destination is a fact about a
 * whole group of codes. One resolver rather than a test at each render site, so
 * “the more specific paragraph REPLACES the less specific one” is a property of
 * the data rather than a rule someone has to remember — and so a second
 * paragraph sending one reader to two places is not expressible.
 */
function placesCopyFor(
  code: string,
  destination: XeroRefusalDestination,
  origin: XeroTaxRefusalOrigin | null,
): PlacesCopy | undefined {
  const fromSource = origin ? sourcePlacesFor(origin) : undefined;
  return fromSource ?? REFUSAL_PLACES.get(code) ?? DESTINATION_PLACES[destination];
}

/**
 * A refused create, rendered at the screen that can actually resolve it.
 *
 * ⚠️ Nothing here offers a retry, on any branch — every code is
 * `retryable: false` (typed as the literal, not just the value), and each
 * names a state where repeating the identical request reproduces it
 * identically. Once failed twice, a retry invites the reader to start
 * changing things at random until it goes through.
 *
 * ⚠️ EXPORTED so there is ONE renderer for a refusal, not two: `QuoteDetailPage`
 * reports the automatic push that follows a save, and its refusal routes to
 * exactly the same places as a refused create — the destination is a property
 * of the CODE, not of what triggered the request.
 *
 * ⚠️ NOT used for the two outcomes that read differently — see
 * `isSettledInXeroCode` and `isGoneInXeroCode` in `src/lib/xero.ts`. Both are
 * `destination: 'nowhere'`, which this banner tones as a terminal failure
 * (`role="alert"`) — right for a button someone pressed, wrong for a push
 * nobody asked for, where "Xero has already sent it" is the ordinary answer.
 * Those two are toned by their caller and never reach here.
 */
export function RefusalBanner({
  code,
  refusal,
  origin,
  lines,
  company,
  entity = 'quotes',
  alreadyInXero = false,
  onFixCompanyDefaults,
}: {
  code: string;
  refusal: XeroQuoteRefusal;
  /** Which record supplied the refused tax code, where the server says. `null` on every other refusal, on `XERO_NO_TAX_TYPE`, and on an older server — every branch below renders exactly what it did before the field existed. */
  origin: XeroTaxRefusalOrigin | null;
  /** The server's own message, already de-duplicated by `xeroErrorLines`. */
  lines: string[];
  company: { id: number; name: string; billingName?: string | null } | null;
  /**
   * WHICH kind of record this refusal is about. Every noun in the copy comes
   * from `XERO_ENTITIES[entity].label` — see {@link RefusalCopy}.
   *
   * ⚠️ Optional, defaulting to `quotes`, as a compatibility shim: `QuoteDetailPage`
   * also renders this banner (for the automatic push after a save) and predates
   * this prop, so a required prop wouldn't have compiled. Still a trap — a new
   * caller that forgets it silently gets "quote". `XeroPanel` passes it
   * explicitly; do the same, and delete the default once `QuoteDetailPage`
   * passes `entity="quotes"`.
   */
  entity?: XeroEntity;
  /**
   * Whether this record is ALREADY in Xero — read off the link row, never
   * guessed from a status and never from `xeroQuoteNumber`, which survives an
   * unlink and can name another quote's document.
   *
   * Changes only the closing instruction: this banner serves both the "create
   * in Xero" button and the automatic push on saving a linked quote, and
   * "then create it in Xero" is unfollowable on the second. Defaults to
   * `false` so an existing caller keeps its create wording.
   */
  alreadyInXero?: boolean;
  onFixCompanyDefaults?: () => void;
}) {
  /*
   * A terminal refusal is toned as a failure and a fixable one as something to
   * go and do. Never the only signal — the headline says which it is in words,
   * and the tone only agrees with it.
   */
  const terminal = refusal.destination === 'nowhere';
  /*
   * The ONE place a noun is read, and it is read from the registry. Everything
   * below takes it from `copy`; nothing below writes the word itself.
   */
  const noun = XERO_ENTITIES[entity].label;
  /* The Billing name (the Xero Contact Name), owner 2026-10-25 — never `company.name` in Xero UI. */
  const companyName = company ? documentCustomerName(company) : null;
  const companyLabel = companyName ?? `the company this ${noun} bills`;
  const copy: RefusalCopy = { noun, company: companyLabel, alreadyInXero };
  const explain = refusal.verbatim ? undefined : REFUSAL_EXPLANATIONS.get(code);
  /*
   * ⚠️ **`source` is the ONE thing here that may change which control is
   * offered, and that is a deliberate exception to the rule two paragraphs
   * down.** A code override changes only WORDS, because it is a build-time claim
   * about every refusal of that code. `source` is the server's statement about
   * THIS refusal, and offering the company's Sales-defaults dialog for a fault
   * the response has just placed on a line item is exactly the failure
   * `SALES_DEFAULT_CODES` was written to prevent: someone changes a correct
   * company default, the quote refuses identically, and a second customer's
   * documents have quietly moved onto a different tax rate.
   */
  const source: XeroTaxSource | null = origin?.source ?? null;
  /*
   * Company-sourced AND no line named. Read off the contract rather than off
   * the code name, so a fifth code behaving the same way needs no edit here.
   * See `refusalOriginOf`.
   *
   * ⚠️ *SUPERSEDED, the JUSTIFICATION and not the expression: "on
   * `XERO_TAX_RATE_MISMATCH` the backend names a line even for an inherited
   * code, because the line's own `taxRate` is what disagrees — so the company
   * is where the code came FROM and not where the fix belongs."* The first
   * clause is still the contract and is still true. The conclusion is not:
   * it held while every line's percentage was typeable, and the owner's ruling
   * of 2026-08-30 made the percentage READ-ONLY on a line whose
   * `taxRateSource` is `'company'`. On those lines the company record is
   * exactly where that figure is changed, so suppressing the button sends the
   * reader to a box they cannot type in.
   *
   * 🔴 **LEFT AS IT WAS, DELIBERATELY, BECAUSE THIS RESPONSE CANNOT TELL THE
   * TWO CASES APART.** `origin` carries `source` / `lineId` / `productName`,
   * and `source` is the provenance of the tax CODE — a different column from
   * the provenance of the RATE, and not derivable from it. A company may hold
   * an `xeroSalesTaxType` with a NULL `xeroSalesTaxRate` — every live company
   * held a null rate when migration 092 shipped, whatever tax type it carried,
   * and the backend only derives the rate from the code on writes made since
   * (`resolveTaxDefaults` in `companies.service.ts`). On such a company
   * `source: 'company'` arrives beside a line whose percentage is fully
   * editable and where the company is NOT the fix. Widening this on `source`
   * alone would send that reader to change a company-wide default to correct
   * one line — the exact failure `SALES_DEFAULT_CODES` is written against.
   *
   * **What would close it, in the order to reach for it:**
   *
   *  1. the backend adding the RATE's provenance to the same detail object
   *     (`taxRefusalDetails`, built in `xero-quote.payload.ts` and its invoice
   *     twin), so the answer stays a fact about THIS refusal — the standard
   *     `source` itself is already held to;
   *  2. failing that, the caller passing the offending line's own
   *     `taxRateSource`, which both detail pages already hold on the row
   *     `origin.lineId` names.
   *
   * Neither is invented here on spec: a prop no caller passes is a feature in
   * a file nothing mounts.
   *
   * ⚠️ **Nobody is STRANDED meanwhile**, which is why this is a routing gap
   * rather than a blocker: the Xero tax code picker on the line stays editable
   * whatever the rate does, and picking a code writes the code and its
   * percentage in ONE patch. `sourcePlacesFor` names that route in both
   * branches.
   */
  const companyIsTheFix = source === 'company' && origin?.lineId == null;
  /*
   * The Sales-defaults dialog, but only where the page has one. A page that has
   * not wired it up still routes the reader — to `/companies/{id}` — rather
   * than leaving a sentence with nothing to act on.
   *
   * ⚠️ It inherits `companyIsTheFix` whole, so the conditional-lock gap
   * recorded there is this expression's gap too: on an
   * `XERO_TAX_RATE_MISMATCH` naming a line, no dialog is offered even where
   * that line's percentage is read-only. Close it where the fact arrives, in
   * `companyIsTheFix` — never by adding a second test here, which is how one
   * routing rule becomes two that disagree.
   */
  const fixInline =
    onFixCompanyDefaults !== undefined &&
    (source === 'line' ? false : companyIsTheFix || SALES_DEFAULT_CODES.has(code));
  /*
   * The destinations whose fix involves the company record at all, and so the
   * ones that end in an action pointing at it. Kept as one test rather than
   * repeated in the JSX so a new destination cannot gain the button by
   * accident — and note the button is NOT the whole fix on
   * `quote-form-or-company`, where the paragraph above it names the quote's own
   * field as the alternative.
   *
   * `source === 'line'` short-circuits every destination, including
   * `company-record`: a response placing the fault on a line outranks a routing
   * decided before the response existed.
   */
  const pointsAtCompany =
    source === 'line'
      ? false
      : companyIsTheFix ||
        refusal.destination === 'company-record' ||
        refusal.destination === 'quote-form-or-company';
  /*
   * ⚠️ The “check both places, we cannot say which” link belongs ONLY to the
   * genuinely ambiguous case. With a `source` in hand it is answered: the
   * company branch is carried by `pointsAtCompany` above, and the line branch
   * must offer no company navigation at all.
   */
  const showAmbiguousCompanyLink =
    source === null && refusal.destination === 'quote-lines-or-company' && company !== null;
  /*
   * Both resolved `source ?? code ?? destination` — increasing specificity, and
   * the same order in both slots so a heading and a paragraph can never be
   * chosen from two different levels. The destination is the coarse routing, per-
   * code copy carries what only one code knows, and `source` carries what only
   * this one response knows. See `REFUSAL_HEADINGS` for why per-code copy beat a
   * seventh destination, and the block above `sourceHeadingFor` for why the
   * source layer replaces the both-places copy instead of joining it.
   *
   * ⚠️ A CODE override still touches neither `pointsAtCompany` nor `fixInline`,
   * and that separation is unchanged: overriding a code's WORDS must never change
   * which control it offers. `XERO_NO_TAX_TYPE` keeps `company-record` and its
   * `source` is always `null`, so it keeps the inline Sales-defaults button it
   * has always had.
   */
  const headingCopy =
    (origin ? sourceHeadingFor(origin) : undefined) ??
    REFUSAL_HEADINGS.get(code) ??
    DESTINATION_HEADINGS[refusal.destination];
  const heading = headingCopy(copy);
  const places = placesCopyFor(code, refusal.destination, origin);

  return (
    <Notice
      tone={terminal ? 'danger' : 'warning'}
      role="alert"
      title={<span className="min-w-0 break-words [overflow-wrap:anywhere]">{heading}</span>}
    >
    <div className="flex flex-col items-start gap-2">

      {/*
       * Our sentence stands in front of the server's, never instead of it — the
       * message is still rendered below, so nothing the server said is lost. On
       * a `verbatim` code there is no sentence of ours at all: the useful half
       * is derived per tenant and a paraphrase would drop exactly that.
       */}
      {explain && (
        <p className="m-0 min-w-0 break-words [overflow-wrap:anywhere]">{explain(copy)}</p>
      )}

      {lines.map((line) => (
        <p
          key={line}
          className={cx(
            'm-0 min-w-0 break-words [overflow-wrap:anywhere]',
            explain && 'text-fg-secondary',
          )}
        >
          {line}
        </p>
      ))}

      {/*
        * ONE slot for “where do I fix this”, resolved by code then destination.
        *
        * `copy.company` is `companyLabel` throughout, never `company.name`, so a
        * record with no company still reads correctly and no id is invented.
        * (That state is itself a refusal, `XERO_QUOTE_NO_COMPANY`, but this
        * banner must not depend on that being true elsewhere.)
        */}
      {places && (
        <p className="m-0 min-w-0 break-words text-fg-secondary [overflow-wrap:anywhere]">
          {places(copy)}
        </p>
      )}

      {/*
        * The link is navigation, not a diagnosis, and it is dropped entirely
        * when there is no company to name rather than inventing one. Kept out of
        * `pointsAtCompany` deliberately: with no `source` this destination must
        * NOT offer the Sales-defaults dialog — see `SALES_DEFAULT_CODES` — so it
        * needs a link of its own wording rather than the shared button-or-link
        * below.
        *
        * ⚠️ It is now gated on `source === null` as well, and that gate is what
        * keeps ONE action on screen. With `source: 'company'` the same reader is
        * served by `pointsAtCompany` below, and rendering both would put two
        * company affordances in one banner; with `source: 'line'` the company is
        * ruled out and this link would contradict the paragraph above it.
        */}
      {showAmbiguousCompanyLink && company !== null && (
        <TextLink
          to={`/companies/${company.id}`}
          className="min-w-0 font-medium break-words [overflow-wrap:anywhere]"
        >
          Check the Sales defaults on {companyLabel}
        </TextLink>
      )}

      {pointsAtCompany &&
        (fixInline ? (
          /* `secondary`, not `primary`: one primary per region, and the panel
             footer's send/retry is that panel's primary. */
          <Button size="sm" variant="secondary" className="max-w-full" onClick={onFixCompanyDefaults}>
            {/* Names the record it edits: this button leaves the current record behind. */}
            <span className="min-w-0 truncate">
              {companyName !== null ? `Set them on ${companyName}` : 'Set them on the company'}
            </span>
          </Button>
        ) : (
          company !== null && (
            <TextLink
              to={`/companies/${company.id}`}
              className="min-w-0 font-medium break-words [overflow-wrap:anywhere]"
            >
              Open {companyLabel}
            </TextLink>
          )
        ))}
    </div>
    </Notice>
  );
}

/** Timestamps are true instants; only their absence needs the em dash. */
function dateTimeOrDash(iso: string | null): string {
  return iso ? formatDateTime(iso) : orDash(null);
}

function Term({ children }: { children: ReactNode }) {
  return (
    <dt className="text-xs font-medium text-fg-subtle">{children}</dt>
  );
}

function Value({ children }: { children: ReactNode }) {
  return <dd className="m-0 mt-1 text-sm text-fg-secondary [overflow-wrap:anywhere]">{children}</dd>;
}

/** Drawn by hand — this app carries no icon dependency. */
function ExternalIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3 w-3 shrink-0"
    >
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx(
        'h-3.5 w-3.5 shrink-0 transition-transform duration-150 ease-out motion-reduce:transition-none',
        open && 'rotate-90',
      )}
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}
